import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LetDirective } from '@directives/app-let.directive';
import { Store } from '@ngrx/store';
import { selectIsModifyPath, selectIsFlightsPage, selectIsHomePage, selectRouteParam } from '@selectors/router.selector';
import { SearchFooterComponent } from './components/search-footer/search-footer.component';
import { SearchHeaderComponent } from './components/search-header/search-header.component';
import { PreviousSearchComponent } from './components/previous-search/previous-search.component';
import { SearchService } from './services/search.service';
import { AppCopy } from '@models/cms/app.model';
import { CmsSubKeys } from '@constants/cms.constants';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DATE_FORMAT_DEFAULT, DATE_FORMAT_YEAR_MONTH_DATE, DateFormats } from '@constants/date-format.constants';
import { TripType, MULTI_CITY_MAX_TRIP_COUNT, DEFAULT_MIN_DATE, DEFAULT_MAX_DATE } from '@constants/home.constants';
import { FeatureFlagsEnum } from '@models/environment.type';
import { createDateFromDateLike } from '@utils/data-utils';
import { DateTime } from 'luxon';
import { Observable, startWith, map, combineLatest, filter, BehaviorSubject, Subject, switchMap, take, skip, tap, takeUntil, Subscription } from 'rxjs';
import { CULTURE_ROUTE_PARAM, CultureKeyType, FALLBACK_CULTURE } from '@constants/app.constants';
import { selectPnrExists, selectAppAutoSearch, selectShowModifySearchWidget } from '@selectors/app.selector';
import { selectSearchInputState, selectAvailabilityTripTypeState, selectSearchInputCriteriaState, selectRouteSchedule, selectSearchInputPassengerState, selectHasInvalidPromoCode } from '@selectors/availability.selector';
import { selectSelectedCurrencyCode, selectGeolocatedCountryCode } from '@selectors/culture.selector';
import { selectStationsState, selectStationsStateNoMacs, selectStationsWithConnectionsState, selectStationsWithConnectionsStateNoMacs, selectN0StationsWithConnections, selectN0StationsWithConnectionsNoMacs, selectDohopFeatureStations } from '@selectors/station.selector';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { SearchWidgetFormConfigService } from '@services/form-configs/search-widget-form-config/search-widget-form-config.service';
import { SearchRequest, StationCriteria } from '@models/requests/search-request.model';
import { CalendarDateCssClass, RouteSchedule, RouteScheduleDate } from '@models/requests/route-schedule-request.model';
import { Station } from '@models/responses/station-response.model';
import { PassengerTypeCode } from '@models/booking.models';
import { availabilityGetRouteSchedule, setIsSearchByQueryState } from '@actions/availability.action';
import { DateRangePickerService } from '@components/controls/date-range-picker/date-range-picker.service';
import { TravelerPickerComponent } from '@components/traveler-picker/traveler-picker.component';
import { NullableString } from '@models/app.models';
import { AutoSearch } from '@models/auto-search.model';
import { SearchWidgetCopy } from '@models/cms/search-widget.model';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { SanitizerValueType } from '@pipes/safe-html/safe-html.pipe';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { ModalService } from '@services/modal/modal.service';
import { eachDayOfInterval } from '@utils/date-utils';
import { StationPicker, StationControl, DatesPicker } from 'src/app/interfaces/search-widget-interface';
import { DateRangePickerComponent } from '@components/controls/date-range-picker/date-range-picker.component';
import { InputComponent } from '@components/controls/input/input.component';
import { DatesPickerComponent } from '@components/dates-picker/dates-picker.component';
import { StationPickerComponent } from '@components/station-picker/station-picker.component';
import { TripTypePickerComponent } from '@components/trip-type-picker/trip-type-picker.component';
import { InputDirective } from '@directives/input/input.directive';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    CommonModule,
    LetDirective,
    SearchFooterComponent,
    SearchHeaderComponent,
    PreviousSearchComponent,
    ReactiveFormsModule,
    DatesPickerComponent,
    StationPickerComponent,
    TravelerPickerComponent,
    TripTypePickerComponent,
    InputComponent,
    InputDirective,
    DateRangePickerComponent,
    RouterModule,
  ],
  templateUrl: 'search.component.html',
  styleUrls: ['search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private subscription = new Subscription();
  private dateRangePickerService = inject(DateRangePickerService);

  @ViewChild(TravelerPickerComponent)

  travelerPickerComponent: TravelerPickerComponent;

  @Input() isFlightsPage = false;
  @Input() isHomePage = true;
  @Input() cmsCopy: SearchWidgetCopy;
  @Input() appCopy: AppCopy;
  @Input() background?: boolean | null = false;

  searchWidgetForm: FormGroup;
  private dateRangePickerDepartDate$ = new BehaviorSubject<DateTime | null>(
    null,
  );
  private destinationStationChanges$ = new Subject<void>();

  stations$ = this.featureFlagService.isFeatureActive(
    FeatureFlagsEnum.ALLOW_MACS,
  )
    ? this.store.select(selectStationsState)
    : this.store.select(selectStationsStateNoMacs);
  stationsWithConnections$ = this.featureFlagService.isFeatureActive(
    FeatureFlagsEnum.ALLOW_MACS,
  )
    ? this.store.select(selectStationsWithConnectionsState)
    : this.store.select(selectStationsWithConnectionsStateNoMacs);
  norseStations$ = this.featureFlagService.isFeatureActive(
    FeatureFlagsEnum.ALLOW_MACS,
  )
    ? this.store.select(selectN0StationsWithConnections)
    : this.store.select(selectN0StationsWithConnectionsNoMacs);
  dohopStations$ = this.store.select(selectDohopFeatureStations);
  searchInput$ = this.store.select(selectSearchInputState);
  tripType$ = this.store.select(selectAvailabilityTripTypeState);
  isModifyPath$ = this.store.select(selectIsModifyPath);
  isFlightsPage$ = this.store.select(selectIsFlightsPage);
  isHomePage$ = this.store.select(selectIsHomePage);
  doesPnrExist$ = this.store.select(selectPnrExists);
  autoSearch$ = this.store.select(selectAppAutoSearch);
  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;
  lastSearchedCriteria$ = this.store.select(selectSearchInputCriteriaState);
  routeSchedule$ = this.store.select(selectRouteSchedule);
  selectedCurrencyCode$ = this.store.select(selectSelectedCurrencyCode);
  geolocatedCountryCode$ = this.store.select(selectGeolocatedCountryCode);
  isModifyWidget$ = this.store.select(selectShowModifySearchWidget);
  passengers$ = this.store.select(selectSearchInputPassengerState);
  hasInvalidPromoCode$ = this.store.select(selectHasInvalidPromoCode);

  activeStations: Station[] | null = null;
  stations: Station[] | null = null;
  stationsWithConnections: Station[] | null = null;
  departDates: (Date[] | undefined)[] = [];
  returnDates: Date[] | undefined;
  departDateClasses: Array<CalendarDateCssClass[] | undefined> = [];
  returnDateClasses: CalendarDateCssClass[] | undefined;
  selectedRouteSchedule: Array<RouteScheduleDate[] | undefined> = [];
  onDestinationChangedSubmitTriggered = false;
  formInitialized = false;

  onScheduleExists$ = new BehaviorSubject<boolean>(false);
  onScheduleSet$ = new BehaviorSubject<boolean>(false);

  readonly FeatureFlagsEnum = FeatureFlagsEnum;
  readonly FALLBACK_CULTURE = FALLBACK_CULTURE;
  readonly DEFAULT_MIN_DATE = DEFAULT_MIN_DATE;
  readonly DEFAULT_MAX_DATE = DEFAULT_MAX_DATE;
  readonly DateTime = DateTime;
  readonly DateFormats = DateFormats;
  readonly SanitizerValueType = SanitizerValueType;
  readonly gbf = GrowthBookFeatureKeys;
  readonly CmsSubKeys = CmsSubKeys;

  format = DATE_FORMAT_DEFAULT;
  minDates: Date[] = [];
  selectedOriginStation = [''];
  selectedOriginStationCode = [''];
  selectedDestinationStation = [''];
  selectedDestinationStationCode = [''];
  outSideClick = false;
  lastSearchClicked = false;
  childDobs: Date[] = [];
  infantDobs: Date[] = [];
  searchRequest: SearchRequest | null = null;
  tripType: TripType;

  get tripTypePromoCntl(): FormGroup {
    return <FormGroup>this.searchWidgetForm.get('tripTypePromoCode');
  }

  get tripTypeCntl(): FormControl {
    return <FormControl>this.tripTypePromoCntl.get('tripType');
  }

  get promoCodeCntl(): FormControl {
    return <FormControl>this.tripTypePromoCntl.get('promoCode');
  }

  get criteriaCntl(): FormGroup {
    return <FormGroup>this.searchWidgetForm?.get('criteria');
  }

  get stationsCtrl(): FormArray {
    return <FormArray>this.criteriaCntl?.get('stations');
  }

  get datesCtrl(): FormArray {
    return <FormArray>this.criteriaCntl?.get('dates');
  }

  get travelersCtrl(): FormGroup {
    return <FormGroup>this.searchWidgetForm?.get('travelers');
  }

  get scheduleLoadedThru(): string {
    if (isNaN(+this.cmsCopy.scheduleLoadedThruDays)) {
      return this.cmsCopy.scheduleLoadedThruDate;
    }

    const thruDays = DateTime.now()
      .plus({ days: +this.cmsCopy.scheduleLoadedThruDays })
      .startOf('day');
    const thruDate = DateTime.fromFormat(
      this.cmsCopy.scheduleLoadedThruDate,
      DATE_FORMAT_YEAR_MONTH_DATE,
    ).startOf('day');

    return thruDays < thruDate
      ? thruDays.toFormat(DATE_FORMAT_YEAR_MONTH_DATE)
      : thruDate.toFormat(DATE_FORMAT_YEAR_MONTH_DATE);
  }

  get scheduleLoadedThruDateAsDate(): Date | undefined {
    return createDateFromDateLike(
      this.scheduleLoadedThru,
      DATE_FORMAT_YEAR_MONTH_DATE,
    );
  }

  get isDatePickerSingleDate(): Observable<boolean> {
    return this.tripTypeCntl.valueChanges.pipe(
      startWith(this.tripType),
      map((t) => t === TripType.oneWay)
    );
  }

  constructor(
    public searchService: SearchService,
    public modalService: ModalService,
    public store: Store,
    public featureFlagService: FeatureFlagService,
    private formConfigService: SearchWidgetFormConfigService,
    private cd: ChangeDetectorRef,
    public gbs: GrowthBookService,
  ) {}

  ngOnInit(): void {
    this.initGrowthBookChangeDetector();
    this.initSearchFormCreatorListener();
    this.initRouteScheduleListener();
    this.resetDateRangePickerOnChangesInputs();
    this.onDestinationChangedSubmitTriggeredListener();
  }

  ngAfterViewInit(): void {
    this.hasInvalidPromoCodeListener();
    this.isHomePageListener();
    this.onPromoCodeChangeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initGrowthBookChangeDetector(): void {
    this.gbs.onChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.searchWidgetForm) {
          this.searchService.setPromoCode(
            this.promoCodeCntl,
            this.cmsCopy,
          );
        }
        this.cd.detectChanges();
      });
  }

  private initSearchFormCreatorListener() {
    this.searchService.initSearchForm(
      this.stations$,
      this.stationsWithConnections$,
      this.culture$,
      this.autoSearch$,
      this.searchInput$,
      this.tripType$,
      this.lastSearchedCriteria$,
      this.selectedCurrencyCode$,
      this.geolocatedCountryCode$,
      this.destroy$,
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe(
        ([
          [stations, stationsWithConnections, culture, autoSearch, tripType],
          searchReq,
          lastSearchedCriteria,
          selectedCurrencyCode,
          countryCode,
        ]) => {
          this.tripType = tripType;
          this.stations = stations ?? null;
          this.stationsWithConnections = stationsWithConnections ?? null;
          this.activeStations = this.featureFlagService.isFeatureActive(
            FeatureFlagsEnum.CONNECTIONS,
          )
            ? this.stationsWithConnections ?? this.stations
            : this.stations;
          if (searchReq && tripType) {
            this.searchRequest = searchReq;

            if (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !this.searchWidgetForm?.value.criteria.stations[0]
                .originStationCode ||
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !this.searchWidgetForm?.value.criteria.stations[0]
                .destinationStationCode
            ) {
              this.searchWidgetForm =
                this.formConfigService.generateSearchWidgetForm(
                  this.activeStations ?? [],
                  searchReq,
                  tripType,
                );
            }

            if (
              tripType === TripType.multiCity &&
              searchReq?.criteria?.[0]?.beginDate
            ) {
              this.minDates = [...this.minDates];
              this.minDates[1] = DateTime.fromISO(
                searchReq.criteria[0].beginDate,
              ).toJSDate();
            }

            this.searchRequest.criteria?.forEach((c, i) => {
              const originStation = this.stations?.find(
                (s) => s.stationCode === c.origin,
              );

              const destStation = this.stations?.find(
                (s) => s.stationCode === c.destination,
              );

              originStation?.fullName &&
                originStation?.stationCode &&
                this.onOriginStationChange(
                  {
                    stationName: originStation.fullName,
                    stationCode: originStation.stationCode,
                  },
                  i,
                );
              destStation?.fullName &&
                destStation?.stationCode &&
                this.onDestinationStationChange(
                  {
                    stationName: destStation.fullName,
                    stationCode: destStation.stationCode,
                  },
                  i,
                );
            });
            this.infantDobs =
              searchReq.passengers
                ?.find((p) => p.type === PassengerTypeCode.INF)
                ?.dobs?.map((dob) => DateTime.fromISO(dob).toJSDate()) ?? [];

            this.childDobs =
              searchReq.passengers
                ?.find((p) => p.type === PassengerTypeCode.CHD)
                ?.dobs?.map((dob) => DateTime.fromISO(dob).toJSDate()) ?? [];
          } else if (!this.searchWidgetForm) {
            this.searchWidgetForm =
              this.formConfigService.generateSearchWidgetForm(
                this.activeStations ?? [],
              );
          }
          if (autoSearch.search && culture && !this.isHomePage) {
            // search success will set this back to false in the reducer
            this.onSubmit(
              selectedCurrencyCode,
              culture,
              lastSearchedCriteria,
              countryCode ?? undefined,
              autoSearch,
            );
          }

          if (this.searchWidgetForm) {
            this.tripTypeCntl.setValue(tripType);
            for (let i = 0; i < MULTI_CITY_MAX_TRIP_COUNT; i++) {
              this.minDates.push(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                i > 0 && this.getDepartDateCntl(i - 1)?.value
                  ? this.getDepartDateCntl(i - 1).value
                  : DEFAULT_MIN_DATE,
              );
            }

            this.tripTypeCntl.valueChanges
              ?.pipe(takeUntil(this.destroy$))
              .subscribe((s: TripType) => this.tripTypeChange(s));
            this.searchService.setPromoCode(
              this.promoCodeCntl,
              this.cmsCopy,
            );
            this.cd.detectChanges();
            this.formInitialized = true;
          }
        },
    );
  }

  private initRouteScheduleListener() {
    combineLatest([this.routeSchedule$, this.onScheduleExists$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([schedule]) => {
        const selectedStations =
          this.stationsCtrl?.getRawValue() as StationPicker[];
        selectedStations?.forEach((stations, i) => {
          this.selectedRouteSchedule[i] =
            schedule?.[
              `${stations.originStationCode}-${stations.destinationStationCode}`
            ];
          if (this.selectedRouteSchedule[i]) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.departDates[i] = this.selectedRouteSchedule[i]!.map(
              (d) => d.scheduleDay,
            );
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.departDateClasses[i] = this.selectedRouteSchedule[i]!.map(
              (d) => ({
                date: d.scheduleDay,
                classes: d.isDohop ? ['day-dohop'] : ['day-norse'],
              }),
            );

            const departCtrl = this.getDepartDateCntl(i);
            const returnCtrl = this.getReturnDateCntl(i);

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const firstFlight = this.selectedRouteSchedule[i]![0];
            const returnFlight = firstFlight && schedule?.[`${stations.destinationStationCode}-${stations.originStationCode}`]?.find(s => s.scheduleDay > firstFlight.scheduleDay);
            if (firstFlight) {
              departCtrl.setValue(firstFlight.scheduleDay);
              //Updates minDate validtion for return date
              this.cd.detectChanges();
              returnCtrl.setValue(returnFlight?.scheduleDay ?? firstFlight.scheduleDay);
              this.onScheduleSet$.next(true);
            }
          } else {
            this.departDates[i] = undefined;
            this.departDateClasses[i] = undefined;
            this.onScheduleSet$.next(true);
          }
        });

        this.cd.detectChanges();
      });
  }

  private hasInvalidPromoCodeListener() {
    this.hasInvalidPromoCode$
      .pipe(
        filter((x) => x),
        takeUntil(this.destroy$),
      )
      .subscribe((hasInvalidPromoCode) => {
        if (hasInvalidPromoCode) {
          this.promoCodeCntl.reset();
        }
      });
  }

  private onDestinationChangedSubmitTriggeredListener() {
    this.subscription.add(
      this.searchService.onDestinationChangedSubmitTriggered$.subscribe(
        (value: boolean) => {
          this.onDestinationChangedSubmitTriggered = value;
        }
      )
    );
  }

  private onPromoCodeChangeListener() {
    this.promoCodeCntl.valueChanges.subscribe((promoCodeValue: string | null) => {
      if (promoCodeValue) {
        const uppercasedValue = promoCodeValue.toUpperCase();
        this.promoCodeCntl.setValue(uppercasedValue, { emitEvent: false });
      }
    });
  }

  private isHomePageListener() {
    this.isHomePage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isHomePage) => {
        if (isHomePage) {
          this.searchService.setDestinationChangedSubmitTriggered(false);
        }
      });
  }

  private triggerOnSubmit(index: number) {
    combineLatest([this.autoSearch$, this.searchInput$])
      .pipe((take(1)))
      .subscribe(([autoSearch, searchInput]) => {
        if (!this.formInitialized) {
          return;
        }

        if (!this.selectedOriginStationCode[index] || !this.selectedDestinationStationCode[index]) {
          return;
        }

        if (!this.checkIfRemoveCtaButtonFeatureIsOn(autoSearch.search, searchInput)) {
          return;
        }

        if (this.onDestinationChangedSubmitTriggered) {
          return;
        }

        this.searchService.setDestinationChangedSubmitTriggered(true);

        this.waitForRouteScheduleUpdate()
          .pipe(
            switchMap(() =>
              combineLatest([
                this.lastSearchedCriteria$,
                this.selectedCurrencyCode$,
                this.culture$,
                this.geolocatedCountryCode$,
                this.isModifyPath$
              ])
            ),
            take(1)
          ).subscribe((
          [
            lastSearchedCriteria,
            selectedCurrencyCode,
            culture,
            geolocatedCountryCode,
            isModifyPath
          ]
        ) => {
          this.onScheduleSet$.next(false);

          this.getStationsCtrl(index).updateValueAndValidity();

          this.onSubmit(
            selectedCurrencyCode,
            culture ?? FALLBACK_CULTURE,
            lastSearchedCriteria ?? undefined,
            geolocatedCountryCode ?? undefined,
            undefined,
            isModifyPath ?? false
          );
        });
      });
  }

  private waitForRouteScheduleUpdate() {
    return this.onScheduleExists$.pipe(
      switchMap(scheduleExists =>
        this.onScheduleSet$.pipe(
          filter(value => value),
          scheduleExists ? tap() : skip(1),
          take(1),
        )
      )
    );
  }

  getStationsCtrl(index: number): FormGroup {
    return <FormGroup>this.stationsCtrl.get(`${index}`);
  }

  isValidStationCtrl(index: number): boolean {
    return this.getStationsCtrl(index).valid;
  }

  getStationOriginStationCode(index: number): string {
    return (
      (this.getStationsCtrl(index)?.get('originStationCode')
        ?.value as string) ?? ''
    );
  }

  getStationDestinationStationCode(index: number): string {
    return (
      (this.getStationsCtrl(index)?.get('destinationStationCode')
        ?.value as string) ?? ''
    );
  }

  getDatesCtrl(index: number): FormGroup {
    return <FormGroup>this.datesCtrl.get(`${index}`);
  }

  getTravelersCtrl() {
    return this.travelersCtrl?.controls;
  }

  getDepartDateCntl(index: number): FormControl {
    return <FormControl>this.getDatesCtrl(index)?.get('departDate');
  }

  getReturnDateCntl(index: number): FormControl {
    return <FormControl>this.getDatesCtrl(index)?.get('returnDate');
  }

  onOriginStationChange(event: StationControl, index: number): void {
    this.selectedOriginStation[index] = event?.stationName;
    this.selectedOriginStationCode[index] = event?.stationName
      ? event?.stationCode
      : '';
    this.fetchRouteSchedule(index);
  }

  onDestinationStationChange(event: StationControl, index: number): void {
    this.selectedDestinationStation[index] = event?.stationName;
    this.selectedDestinationStationCode[index] = event?.stationName
      ? event?.stationCode
      : '';

    this.destinationStationChanges$.next();
    this.fetchRouteSchedule(index);

    this.triggerOnSubmit(index);
  }

  checkIfRemoveCtaButtonFeatureIsOn(isFromMetaSearch: boolean, searchInput: SearchRequest | null) {
    if (isFromMetaSearch || !this.isHomePage || searchInput?.criteria.some(c => c.destination)) {
      return false;
    }

    if (this.gbs.growthBook.isOn(this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE)
        && this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE, '') === 'b') {
      return false;
    }

    return this.gbs.growthBook.isOn(this.gbf.FEAT_498_REMOVE_CTA_SEARCH_FRONTPAGE)
      && this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_498_REMOVE_CTA_SEARCH_FRONTPAGE, '') === 'b';
  }

  onSubmit(
    currencyCode: NullableString,
    culture: CultureKeyType,
    previousCriteria: StationCriteria[] | undefined,
    countryCode: string | undefined,
    autoSearch?: AutoSearch,
    isModifyPath?: boolean,
  ): void {
    const selectedStations = this.stationsCtrl.value as StationPicker[];
    const selectedDates = this.datesCtrl.value as DatesPicker[];

    // Update of dates based on previous criteria or reset of the search flag
    !autoSearch?.search
      ? this.store.dispatch(setIsSearchByQueryState({ state: false }))
      : this.searchService.updateDatesFromPreviousCriteria(previousCriteria, this.datesCtrl);

    // Checking if the selected date exceeds the available flight schedule
    if (this.searchService.isDateBeyondSchedule(selectedDates, this.scheduleLoadedThru)) {
      this.searchService.showScheduleErrorModal(this.cmsCopy, this.scheduleLoadedThru);
      return;
    }

    // Form validation
    if (!this.searchService.validateFormAndShowErrors(
      this.cmsCopy,
      this.searchWidgetForm,
      this.travelersCtrl,
      this.stationsCtrl,
      this.datesCtrl,
      autoSearch
    )) {
      return;
    }

    // Generation of search criteria
    const criteria = this.searchService.generateCriteria(selectedStations, selectedDates);

    // Validation of route and date
    if (!this.searchService.validateRouteDates(criteria, culture, countryCode, this.cmsCopy, this.activeStations)) {
      return;
    }

    // Adding criteria for return flights in case of a roundTrip
    if (this.tripTypeCntl?.value === TripType.roundTrip) {
      this.searchService.addReturnTripCriteria(criteria, selectedStations, selectedDates);
    }

    // Checking if connections include Dohop
    const isAnyPairDohop = this.searchService.checkIfAnyPairDohop(criteria, this.activeStations);

    // Checking if the schedule date includes Norse connections
    const isAnyRouteScheduleDateNorse = this.searchService.checkIfAnyRouteScheduleDateNorse(criteria, this.selectedRouteSchedule);

    // Initiating the search
    this.searchService.handleSearch(
      currencyCode,
      culture,
      criteria,
      previousCriteria,
      autoSearch,
      isAnyPairDohop,
      isAnyRouteScheduleDateNorse,
      isModifyPath,
      this.activeStations,
      this.travelersCtrl,
      this.cmsCopy,
      this.infantDobs,
      this.childDobs,
      this.lastSearchClicked,
      this.promoCodeCntl.value,
      this.isFlightsPage,
      this.searchRequest,
      this.tripTypeCntl?.value,
      this.isHomePage,
      this.stationsWithConnections,
    );
  }

  onDepartDateChange(index: number): void {
    /* istanbul ignore else */
    if (this.tripTypeCntl?.value === TripType.multiCity) {
      this.minDates = [...this.minDates];
      for (let i = index; i < MULTI_CITY_MAX_TRIP_COUNT; i++) {
        if (i < this.datesCtrl.length) {
          if (i > 0) {
            const previousDateCtrl = this.getDepartDateCntl(i - 1);
            this.minDates[i] = <Date>previousDateCtrl.value;
            const dateCtrl = this.getDepartDateCntl(i);
            if (
              DateTime.fromJSDate(<Date>dateCtrl.value).startOf('day') <
              DateTime.fromJSDate(<Date>previousDateCtrl.value).startOf('day')
            ) {
              dateCtrl.reset();
            }
          } else {
            this.minDates[i] = DEFAULT_MIN_DATE;
          }
        }
      }
    } else if (this.tripTypeCntl?.value === TripType.roundTrip) {
      const departDateCtrl = this.getDepartDateCntl(index);
      const returnDateCtrl = this.getReturnDateCntl(index);
      if (
        DateTime.fromJSDate(<Date>returnDateCtrl.value).startOf('day') <
        DateTime.fromJSDate(<Date>departDateCtrl.value).startOf('day')
      ) {
        returnDateCtrl.reset();
      }
    }
  }

  fetchRouteSchedule(index: number) {
    const originStationCode = this.selectedOriginStationCode[index];
    const destinationStationCode = this.selectedDestinationStationCode[index];
    if (originStationCode && destinationStationCode) {
      this.routeSchedule$.pipe(take(1)).subscribe((s) => {
        if (!s?.[`${originStationCode}-${destinationStationCode}`]) {
          if (originStationCode !== destinationStationCode) {
            this.onScheduleExists$.next(false);
            this.store.dispatch(
              availabilityGetRouteSchedule({
                request: {
                  origin: originStationCode,
                  destination: destinationStationCode,
                  beginDate: DateTime.now()
                    .startOf('day')
                    .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                  endDate: this.scheduleLoadedThru,
                },
              }),
            );
          }
        } else {
          this.onScheduleExists$.next(true);
        }
        if (this.tripTypeCntl.value === TripType.roundTrip && index != 1) {
          if (!s?.[`${destinationStationCode}-${originStationCode}`]) {
            this.onScheduleExists$.next(false);
            this.store.dispatch(
              availabilityGetRouteSchedule({
                request: {
                  origin: destinationStationCode,
                  destination: originStationCode,
                  beginDate: DateTime.now()
                    .startOf('day')
                    .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                  endDate: this.scheduleLoadedThru,
                },
              }),
            );
          }
        }
      });
    }
  }

  onDateRangePickerChanges(
    index: number,
    dates: { firstDate?: DateTime; secondDate?: DateTime; isSkipped: boolean },
  ) {
    this.getFirstDateForDateRangePicker(index)
      .pipe(take(1))
      .subscribe((d) => {
        this.getDepartDateCntl(index).setValue(
          dates.isSkipped || (!dates.firstDate && !dates.secondDate) ?
            d?.toJSDate() :
            dates.firstDate?.toJSDate(),
        );

        this.getReturnDateCntl(index).setValue(
          dates.isSkipped || (!dates.firstDate && !dates.secondDate) ?
            d?.toJSDate() :
            dates.secondDate?.toJSDate(),
        );

        this.dateRangePickerDepartDate$.next(
          dates.firstDate && dates.secondDate ? null : dates.firstDate ?? null,
        );

        this.getReturnDateCntl(index).updateValueAndValidity();
      });
  }

  getFirstDateForDateRangePicker(i: number) {
    const cityPair = `${this.selectedOriginStationCode[i]}-${this.selectedDestinationStationCode[i]}`;
    return this.getAvailableDatesForDateRangePicker(cityPair).pipe(
      map((d) => d[0]),
    );
  }

  getLastDateForDateRangePicker(i: number) {
    const cityPair = `${this.selectedDestinationStationCode[i]}-${this.selectedOriginStationCode[i]}`;
    return this.getAvailableDatesForDateRangePicker(cityPair).pipe(
      map((d) => d[d.length - 1]),
    );
  }

  getDisabledDatesForDepartDate(i: number) {
    return this.routeSchedule$.pipe(
      map((routeSchedule) => {
        const cityPair = `${this.selectedOriginStationCode[i]}-${this.selectedDestinationStationCode[i]}`;
        return this.getDisabledDatesForDateRangePickerByCityPair(
          routeSchedule,
          cityPair,
        );
      }),
    );
  }

  getDisabledDatesForReturnDate(i: number) {
    return this.routeSchedule$.pipe(
      map((routeSchedule) => {
        const cityPair = `${this.selectedDestinationStationCode[i]}-${this.selectedOriginStationCode[i]}`;
        return this.getDisabledDatesForDateRangePickerByCityPair(
          routeSchedule,
          cityPair,
        );
      }),
    );
  }

  private tripTypeChange(tripType: TripType): void {
    if (tripType === TripType.multiCity) {
      if (this.stationsCtrl.length === 1) {
        this.stationsCtrl.push(
          this.formConfigService.generateStationsForm(
            this.activeStations ?? [],
          ),
        );
      }

      /* istanbul ignore else */
      if (tripType === TripType.multiCity) {
        if (
          this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS)
        ) {
          this.activeStations = this.stations;
        }
        this.minDates = [...this.minDates];
        for (let i = 0; i < MULTI_CITY_MAX_TRIP_COUNT; i++) {
          this.minDates[i] =
            i > 0 && this.getDepartDateCntl(i - 1)?.value
              ? <Date>this.getDepartDateCntl(i - 1).value
              : DEFAULT_MIN_DATE;
        }
      } else {
        if (
          this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS)
        ) {
          this.activeStations = this.stationsWithConnections;
        }
      }

      if (this.datesCtrl.length === 1 && this.minDates[1]) {
        this.datesCtrl.push(this.formConfigService.generateDatesForm(tripType));
      }
    }

    if (tripType !== TripType.multiCity) {
      if (this.stationsCtrl.length > 1) {
        this.stationsCtrl.removeAt(1);
      }
      if (this.datesCtrl.length > 1) {
        this.datesCtrl.removeAt(1);
      }
    }

    if (tripType === TripType.roundTrip) {
      this.datesCtrl.controls.forEach((group) => {
        group.get('returnDate')?.setValidators([Validators.required]);
      });
    } else {
      this.datesCtrl.controls.forEach((group) => {
        group.get('returnDate')?.clearValidators();
        group.get('returnDate')?.reset();
      });
    }

    this.stationsCtrl.controls.forEach((_, i) => {
      this.fetchRouteSchedule(i);
    });
  }

  private getAvailableDatesForDateRangePicker(cityPair: string) {
    return this.routeSchedule$.pipe(
      map((routeSchedule) => {
        return (
          routeSchedule?.[cityPair]
            ?.filter((routeScheduleDate) => !routeScheduleDate.isDohop)
            .map((routeScheduleDate) =>
              DateTime.fromJSDate(routeScheduleDate.scheduleDay),
            ) ?? []
        );
      }),
    );
  }

  private getDisabledDatesForDateRangePickerByCityPair(
    routeSchedule: RouteSchedule | null,
    cityPair: string,
  ) {
    if (!routeSchedule) {
      return [];
    }

    const routeScheduleDates = routeSchedule[cityPair];

    if (!routeScheduleDates || routeScheduleDates.length === 0) {
      return [];
    }

    const firstScheduleDay = routeScheduleDates[0]?.scheduleDay;
    const lastScheduleDay =
      routeScheduleDates[routeScheduleDates.length - 1]?.scheduleDay;

    if (!firstScheduleDay || !lastScheduleDay) {
      return [];
    }

    const firstDate = DateTime.fromJSDate(firstScheduleDay);
    const lastDate = DateTime.fromJSDate(lastScheduleDay);
    const allDates = eachDayOfInterval(firstDate, lastDate);

    const availableDates = routeScheduleDates
      .filter((routeScheduleDate) => !routeScheduleDate.isDohop)
      .map((routeScheduleDate) =>
        DateTime.fromJSDate(routeScheduleDate.scheduleDay),
      );

    return allDates.filter(
      (date) =>
        !availableDates.map((d) => d.toMillis()).includes(date.toMillis()),
    );
  }

  private resetDateRangePickerOnChangesInputs() {
    combineLatest([
      this.destinationStationChanges$,
      this.tripType$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dateRangePickerService.onResetState({
          all: false,
          resetFirstDateFromRange: true,
          resetLastDateFromRange: true,
        });
      });
  }
}
